import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FiRefreshCw,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
} from "react-icons/fi";

import {
  getPriceDashboard,
  confirmDashboardNoChange,
} from "../api_controll/apiAdmin";
import ExportPricesModal from "./ExportPricesModal";

import "../styles/price-dashboard.css";

const CATEGORY_LABEL = {
  all: "Vše",
  competitor: "Konkurenční ČSPH",
  armex: "Armex ČSPH",
};

const canConfirmFromDashboard = (item) => {
  return item?.category === "competitor";
};

function formatShortDateTime(value, fallback = "Bez data") {
  if (!value) return fallback;

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}(?::?\d{2})?)?$/
  );

  if (!match) return raw;

  const [, year, month, day, hour, minute] = match;

  return `${day}.${month}. ${hour}:${minute}`;
}

function getRowTypeLabel(category) {
  if (category === "armex") return "Armex";
  if (category === "competitor") return "Konkurence";
  return "Stav";
}

function getRowTypeClass(item) {
  const category = item?.category;
  const changedCount = Number(item?.changed_count ?? 0);

  if (category === "armex") {
    return "price-dashboard-type price-dashboard-type--armex";
  }

  if (category === "competitor" && changedCount > 0) {
    return "price-dashboard-type price-dashboard-type--competitor-alert";
  }

  if (category === "competitor") {
    return "price-dashboard-type price-dashboard-type--competitor";
  }

  return "price-dashboard-type";
}

export default function PriceDashboard({
  onOpenStation,
  onAfterConfirm,
}) {
  const [category, setCategory] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState({});
  const [isExportModalOpen, setExportModalOpen] = useState(false);

  const items = dashboard?.items || [];
  const summary = dashboard?.summary || {};

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPriceDashboard({ category, limit: 10 });
      setDashboard(res?.data || null);
      setSelected({});
      setExpanded({});
    } catch (e) {
      console.error(e);
      toast.error("Nepodařilo se načíst cenový dashboard.");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

const selectableItems = useMemo(() => {
  return items.filter(canConfirmFromDashboard);
}, [items]);

const selectedItems = useMemo(() => {
  return items.filter(
    (item) => selected[item.id] && canConfirmFromDashboard(item)
  );
}, [items, selected]);

const allVisibleSelected =
  selectableItems.length > 0 &&
  selectableItems.every((item) => selected[item.id]);

  const toggleSelect = (itemId) => {
  const item = items.find((row) => row.id === itemId);

    if (!canConfirmFromDashboard(item)) {
        return;
    }

    setSelected((prev) => ({
        ...prev,
        [itemId]: !prev[itemId],
    }));
    };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
        setSelected({});
        return;
    }

    const next = {};

    for (const item of selectableItems) {
        next[item.id] = true;
    }

    setSelected(next);
    };

  const toggleExpanded = (itemId) => {
    setExpanded((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleConfirmNoChange = async () => {
    if (selectedItems.length === 0) {
      toast.warn("Nejdřív vyberte alespoň jednu ČSPH.");
      return;
    }

    const ok = window.confirm(
      `Opravdu potvrdit vybrané ČSPH beze změn? Počet: ${selectedItems.length}`
    );

    if (!ok) return;

    try {
      setConfirming(true);

      const payloadItems = selectedItems.map((item) => ({
        station_id: item.station_id,
        category: item.category,
      }));

      const res = await confirmDashboardNoChange({ items: payloadItems });
      const data = res?.data || res;

      const confirmedCount = data?.confirmed_count ?? 0;
      const failedCount = data?.failed_count ?? 0;

      if (failedCount > 0) {
        toast.warn(`Potvrzeno: ${confirmedCount}, chyb: ${failedCount}`);
      } else {
        toast.success(`Potvrzeno beze změn: ${confirmedCount}`);
      }

      await loadDashboard();
      await onAfterConfirm?.();
    } catch (e) {
      console.error(e);
      toast.error("Potvrzení beze změn selhalo.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <section className="price-dashboard">
      <div className="price-dashboard-header">
        <div>
          <div className="eyebrow">Cenový dashboard</div>
          <h2>Hromadné potvrzení stavů ČSPH</h2>
          <p>
            Přehled aktivních stavů. Pro úpravu cen otevřete detail stanice.
          </p>
        </div>

        <div className="price-dashboard-actions">
          <button
            type="button"
            className="btn btn-compact btn-success"
            onClick={() => setExportModalOpen(true)}
            disabled={loading || confirming}
          >
            Exportovat ceny
          </button>

          <button
            type="button"
            className="btn btn-compact btn-muted"
            onClick={loadDashboard}
            disabled={loading || confirming}
          >
            <FiRefreshCw />
            Obnovit
          </button>

          <button
            type="button"
            className="btn btn-compact btn-primary-soft"
            onClick={handleConfirmNoChange}
            disabled={selectedItems.length === 0 || confirming}
          >
            <FiCheckCircle />
            Potvrdit vybrané beze změn
          </button>
        </div>
      </div>
      {isExportModalOpen && <ExportPricesModal onClose={() => setExportModalOpen(false)} />}

      <div className="price-dashboard-tabs">
        {["all", "competitor", "armex"].map((key) => (
          <button
            key={key}
            type="button"
            className={`price-dashboard-tab ${category === key ? "active" : ""}`}
            onClick={() => setCategory(key)}
          >
            {CATEGORY_LABEL[key]}
          </button>
        ))}
      </div>

      <div className="price-dashboard-summary">
        <div className="price-dashboard-summary-card">
          <span>Celkem aktivní</span>
          <strong>{summary.total_active ?? 0}</strong>
        </div>

        <div className="price-dashboard-summary-card">
          <span>Armex ČSPH</span>
          <strong>{summary.armex_active ?? 0}</strong>
        </div>

        <div className="price-dashboard-summary-card">
          <span>Konkurence</span>
          <strong>{summary.competitor_active ?? 0}</strong>
        </div>

        <div className="price-dashboard-summary-card">
          <span>Vybráno</span>
          <strong>{selectedItems.length}</strong>
        </div>
      </div>

      <div className="price-dashboard-toolbar">
        <button
          type="button"
          className="btn btn-compact btn-muted"
          onClick={toggleSelectAll}
          disabled={items.length === 0}
        >
          {allVisibleSelected ? "Zrušit výběr" : "Vybrat vše ve filtru"}
        </button>

        <span>
          Aktuální filtr: <strong>{CATEGORY_LABEL[category]}</strong>
        </span>
      </div>

      <div className="price-dashboard-card">
        {loading ? (
          <div className="price-dashboard-state">Načítám dashboard…</div>
        ) : items.length === 0 ? (
          <div className="price-dashboard-state">
            Žádné aktivní stavy k potvrzení.
          </div>
        ) : (
          <div className="price-dashboard-table">
            <div className="price-dashboard-row price-dashboard-row--head">
              <div />
              <div>Typ</div>
              <div>ČSPH</div>
              <div>Stav</div>
              <div>Poslední datum</div>
              <div>Změn</div>
              <div>Zdroj</div>
              <div>Akce</div>
            </div>

            {items.map((item) => {
              const isSelected = !!selected[item.id];
              const isExpanded = !!expanded[item.id];

              return (
                <React.Fragment key={item.id}>
                  <div className={`price-dashboard-row ${isSelected ? "selected" : ""}`}>
                    <div>
                     {canConfirmFromDashboard(item) ? (
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(item.id)}
                            />
                            ) : (
                            <span
                                className="price-dashboard-checkbox-placeholder"
                                title="Potvrzení provedení změn se řeší v detailu ČSPH"
                            >
                                —
                            </span>
                            )}
                    </div>

                    <div>
                      <span className={getRowTypeClass(item)}>
                        {getRowTypeLabel(item.category)}
                      </span>
                    </div>

                    <div className="price-dashboard-station">
                      <strong>{item.station_name}</strong>
                      {item.sn && <small>SN: {item.sn}</small>}
                    </div>

                    <div>
                      <strong>{item.label || "Aktivní stav"}</strong>
                      {item.category === "competitor" && item.latest_competitor_price_change_at && (
                        <small>
                          Posl. změna konkurence:{" "}
                          {formatShortDateTime(item.latest_competitor_price_change_at)}
                        </small>
                      )}
                    </div>

                    <div>{formatShortDateTime(item.last_at)}</div>

                    <div>{item.changed_count ?? 0}</div>

                    <div>
                      <span>{item.source_label || "-"}</span>
                      {item.user_label && <small>{item.user_label}</small>}
                    </div>

                    <div className="price-dashboard-row-actions">
                      <button
                        type="button"
                        className="icon-button"
                        title="Rozbalit historii"
                        onClick={() => toggleExpanded(item.id)}
                      >
                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      </button>

                      <button
                        type="button"
                        className="btn btn-compact btn-muted"
                        onClick={() => onOpenStation?.(item.station_id)}
                      >
                        <FiExternalLink />
                        Detail
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="price-dashboard-detail-row">
                      <DashboardRowDetail item={item} dashboard={dashboard} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function DashboardRowDetail({ item, dashboard }) {
  const latestArmex = dashboard?.latest_armex_10 || [];
  const latestCompetitor = dashboard?.latest_competitor_10 || [];

  if (item.category === "armex") {
    const related = latestArmex
      .filter((event) => Number(event.station_id) === Number(item.station_id))
      .slice(0, 10);

    return (
      <div className="price-dashboard-detail">
        <strong>Poslední Armex události</strong>

        {related.length === 0 ? (
          <p>Žádná historie pro tuto ČSPH v posledních událostech.</p>
        ) : (
          <div className="price-dashboard-mini-list">
            {related.map((event) => (
              <div className="price-dashboard-mini-item" key={event.id}>
                <span>{formatShortDateTime(event.display_at || event.event_at)}</span>
                <strong>{formatArmexEventType(event.event_type)}</strong>
                <small>
                  změn {event.changed_count ?? 0}
                  {event.event_by_username ? ` · ${event.event_by_username}` : ""}
                  {event.reset_at ? ` · uzavřeno ${formatShortDateTime(event.reset_at)}` : ""}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const related = latestCompetitor
    .filter((check) => Number(check.station_id) === Number(item.station_id))
    .slice(0, 10);

  return (
    <div className="price-dashboard-detail">
      <strong>Poslední kontroly okolních ČS</strong>

      {related.length === 0 ? (
        <p>Žádná historie pro tuto ČSPH v posledních kontrolách.</p>
      ) : (
        <div className="price-dashboard-mini-list">
          {related.map((check) => (
            <div className="price-dashboard-mini-item" key={check.id}>
              <span>{formatShortDateTime(check.checked_at)}</span>
              <strong>{formatCompetitorSource(check)}</strong>
              <small>
                změn {check.changed_count ?? 0} 
              </small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatArmexEventType(type) {
  const map = {
    user_confirm_executed: "Změny provedeny obsluhou",
    manager_reprice: "Přecenění managerem",
    scheduled_reprice: "Aktivované plánované přecenění",
    manager_no_change: "Potvrzeno beze změn",
  };

  return map[type] || "Událost";
}

function formatCompetitorSource(check) {
  const sourceName = String(check?.source_name || "").trim();
  const sourceType = String(check?.source_type || "").trim();
  const note = String(check?.note || "").trim();
  const source = sourceName || note;

  const map = {
    tank_ono: "Tank ONO",
    user_nearby_check: "Kontrola obsluhou",
    admin_confirm: "Potvrzení adminem",
    admin_no_change: "Potvrzení beze změn",
    tank_ono_price_change_detected: "Tank ONO",
  };

  if (map[source]) return map[source];

  const normalized = source.toLowerCase();

  if (normalized.includes("tank ono")) return "Tank ONO";
  if (sourceType === "automatic") return "Automatická kontrola";
  if (sourceType === "user") return "Kontrola obsluhou";
  if (sourceType === "admin") return "Potvrzení adminem";

  return "Kontrola";
}